import { ReactNode, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchInvoiceInboxConfig,
  fetchSenderRules,
  fetchSupplierArrangements,
  unblockSender,
} from "services/invoice-inbox.service";
import { fetchById as fetchUserById } from "services/user-select.service";
import { CompanyEntity } from "types/Company";
import { InvoiceSenderRule } from "types/InvoiceInbox";
import { formatPrettyDate } from "utils/helperFunctions";
import {
  CatalogTable,
  ChangeLink,
  EditPanel,
  SettingRow,
  SettingsCard,
  useSaveWithToast,
} from "./ui";
import { httpStatusOf, useLoadedSetting } from "./use-loaded-setting";

function NoPermissionNote() {
  const { t } = useTranslation();
  return (
    <p className="text-sm text-gray-600">
      {t("settings_incoming_no_permission")}
    </p>
  );
}

const BADGE_COLORS = {
  amber: "bg-amber-100 text-amber-800",
  green: "bg-green-100 text-green-800",
  gray: "bg-gray-100 text-gray-700",
};

function Badge({
  color,
  children,
}: {
  color: keyof typeof BADGE_COLORS;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${BADGE_COLORS[color]}`}
    >
      {children}
    </span>
  );
}

function ApproverName({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}) {
  const { t } = useTranslation();
  const request = useCallback(
    () => fetchUserById(userId, companyId),
    [userId, companyId]
  );
  const { state } = useLoadedSetting<{
    userEntity: { name: string; isDisabled?: boolean };
  }>(request);

  if (state.status === "loading") return null;
  if (state.status !== "ready")
    return <>{t("settings_incoming_approver_unknown")}</>;
  const { name, isDisabled } = state.data.userEntity;
  // A person who has left is still the approver: say so, so it gets changed.
  return (
    <>
      {isDisabled ? t("settings_incoming_approver_inactive", { name }) : name}
    </>
  );
}

/** Whether the register keeps whoever enters a document apart from whoever approves it. */
function StrictApprovalValue({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const request = useCallback(
    () => fetchInvoiceInboxConfig(companyId),
    [companyId]
  );
  const { state } = useLoadedSetting(request);

  if (state.status === "loading") return null;
  if (state.status === "forbidden") return <NoPermissionNote />;
  if (state.status === "failed") return <>{t("settings_load_failed")}</>;
  return state.data.strictApproval ? (
    <Badge color="green">{t("settings_incoming_strict_on")}</Badge>
  ) : (
    <Badge color="gray">{t("settings_incoming_strict_off")}</Badge>
  );
}

function RulesCard({ company }: { company: CompanyEntity }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const approverId = company.invoiceApproverUser;

  return (
    <SettingsCard>
      <SettingRow
        label={t("settings_incoming_approver_label")}
        description={t("settings_incoming_approver_description")}
        value={
          approverId ? (
            <ApproverName companyId={company.id} userId={approverId} />
          ) : (
            <Badge color="amber">{t("settings_incoming_approver_none")}</Badge>
          )
        }
        action={
          <ChangeLink
            label={
              approverId ? undefined : t("settings_incoming_approver_assign")
            }
            onClick={() => navigate(`/app/${company.id}/settings/roles`)}
          />
        }
      />
      <SettingRow
        label={t("settings_incoming_strict_label")}
        description={t("settings_incoming_strict_description")}
        value={<StrictApprovalValue companyId={company.id} />}
      />
      <SettingRow
        label={t("settings_incoming_mailboxes_label")}
        description={t("settings_incoming_mailboxes_description")}
        action={
          <ChangeLink
            label={t("open")}
            onClick={() =>
              navigate(`/app/${company.id}/invoice-inbox/settings`)
            }
          />
        }
      />
    </SettingsCard>
  );
}

function SuppliersCard({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const request = useCallback(
    () => fetchSupplierArrangements(companyId),
    [companyId]
  );
  const { state } = useLoadedSetting(request);

  const suppliers = state.status === "ready" ? state.data.suppliers : [];
  const yesOrDash = (flag: boolean) => (flag ? t("yes") : "—");

  return (
    <SettingsCard
      title={t("settings_incoming_suppliers_title")}
      description={t("settings_incoming_suppliers_description")}
    >
      {state.status === "forbidden" ? (
        <NoPermissionNote />
      ) : (
        <CatalogTable
          columns={[
            { key: "name", header: t("settings_incoming_suppliers_name") },
            { key: "paid", header: t("settings_incoming_suppliers_paid") },
            {
              key: "monthly",
              header: t("settings_incoming_suppliers_monthly"),
            },
          ]}
          rows={suppliers.map((supplier) => ({
            id: supplier.supplier,
            cells: {
              name: supplier.supplierName,
              paid: yesOrDash(supplier.invoicesArrivePaid),
              monthly: yesOrDash(supplier.expectsMonthlyInvoice),
            },
          }))}
          empty={t("settings_incoming_suppliers_empty")}
          isLoading={state.status === "loading"}
          loadError={
            state.status === "failed" ? t("settings_load_failed") : undefined
          }
        />
      )}
      {state.status === "ready" && state.data.truncated && (
        <p className="mt-3 text-sm text-gray-600">
          {t("settings_incoming_suppliers_truncated")}
        </p>
      )}
    </SettingsCard>
  );
}

interface BlockedSenderRowProps {
  companyId: string;
  rule: InvoiceSenderRule;
  /** Loads the list again. */
  onUnblocked: () => Promise<unknown>;
}

function BlockedSenderRow({
  companyId,
  rule,
  onUnblocked,
}: BlockedSenderRowProps) {
  const { t } = useTranslation();
  const { save, saving, error } = useSaveWithToast();
  const [confirming, setConfirming] = useState(false);

  const date = formatPrettyDate(rule.updatedAt, "lt-LT");
  const description = rule.createdByName
    ? t("settings_incoming_senders_blocked_by", {
        name: rule.createdByName,
        date,
      })
    : t("settings_incoming_senders_blocked_on", { date });

  const unblock = async () => {
    await save(
      async () => {
        try {
          await unblockSender(companyId, rule.id);
        } catch (err) {
          // Somebody else removed the rule first: the list is right after a reload.
          if (httpStatusOf(err) !== 404) throw err;
        }
        await onUnblocked();
      },
      {
        errorMessage: (err) =>
          t(
            httpStatusOf(err) === 403
              ? "settings_incoming_no_permission"
              : "settings_incoming_senders_unblock_failed"
          ),
      }
    );
  };

  return (
    <SettingRow
      label={rule.pattern}
      description={description}
      action={
        confirming ? undefined : (
          <ChangeLink
            label={t("settings_incoming_senders_unblock")}
            onClick={() => setConfirming(true)}
          />
        )
      }
    >
      {confirming && (
        <EditPanel
          onSave={unblock}
          onCancel={() => setConfirming(false)}
          saving={saving}
          error={error}
          saveLabel={t("settings_incoming_senders_unblock")}
        >
          <p className="text-sm text-gray-700">
            {t("settings_incoming_senders_unblock_confirm")}
          </p>
        </EditPanel>
      )}
    </SettingRow>
  );
}

function BlockedSendersCard({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const request = useCallback(() => fetchSenderRules(companyId), [companyId]);
  const { state, reload } = useLoadedSetting(request);

  // The mail intake applies a rule only while it is switched on and has not
  // expired; a trust rule lets mail in instead of holding it back.
  const blocked =
    state.status === "ready"
      ? state.data.rules.filter(
          (rule) =>
            rule.isActive &&
            rule.action !== "trust" &&
            !(rule.expiresAt && rule.expiresAt <= Date.now())
        )
      : [];

  let content: ReactNode;
  if (state.status === "forbidden") {
    content = <NoPermissionNote />;
  } else if (state.status === "failed") {
    content = (
      <p className="text-sm text-gray-600">{t("settings_load_failed")}</p>
    );
  } else if (state.status === "loading") {
    content = null;
  } else if (blocked.length === 0) {
    content = (
      <p className="text-sm text-gray-600">
        {t("settings_incoming_senders_empty")}
      </p>
    );
  } else {
    content = blocked.map((rule) => (
      <BlockedSenderRow
        key={rule.id}
        companyId={companyId}
        rule={rule}
        onUnblocked={reload}
      />
    ));
  }

  return (
    <SettingsCard
      title={t("settings_incoming_senders_title")}
      description={t("settings_incoming_senders_description")}
    >
      {content}
    </SettingsCard>
  );
}

/** The rules of the purchase register: who approves, which suppliers and senders are treated differently. */
export default function IncomingInvoicesSettings({
  companyEntity,
}: {
  companyEntity: CompanyEntity;
}) {
  return (
    <div className="space-y-5">
      <RulesCard company={companyEntity} />
      <SuppliersCard companyId={companyEntity.id} />
      <BlockedSendersCard companyId={companyEntity.id} />
    </div>
  );
}
